import { lazy, ComponentType, LazyExoticComponent } from 'react'

// ============================================================================
// FEATURE CONFIGURATION - SINGLE SOURCE OF TRUTH
// ============================================================================
// All features are defined here. Routes, sidebar, and hooks are auto-generated.
// ============================================================================

export enum FeatureType {
  STANDALONE = 'standalone',
  PARENT = 'parent',
  CHILD = 'child',
  MENU_GROUP = 'menu_group',
}

export enum OrphanStatus {
  INDEPENDENT = 'independent',
  ORPHANED = 'orphaned',
  MULTI_ACCESS = 'multi_access',
}

export interface FeatureDefinition {
  id: string
  name: string
  envKey: string
  type: FeatureType
  orphanStatus?: OrphanStatus
  defaultEnabled: boolean
  route?: string
  mainRoute?: true
  component?: LazyExoticComponent<ComponentType<any>> | ComponentType<any>
  sidebar?: {
    show: boolean
    icon?: string
    order?: number
  }
  parentId?: string
  children?: string[]
  dependencies?: string[]  // Feature requires these features to be enabled
  alternativeAccess?: {
    description: string
    routes: string[]
  }
  apiEndpoints?: string[]
  reduxState?: string[]
  permissionKey?: string
  /**
   * Superuser-only: an identity fact (`GET /user-profile/me/` → `is_admin`), not a
   * permission key. The flag only hides the menu; every page still renders its own
   * "superusers only" state on a 403.
   */
  superuserOnly?: boolean
}

// ============================================================================
// FEATURE DEFINITIONS
// ============================================================================

export const FEATURE_CONFIG: FeatureDefinition[] = [
  // ==========================================================================
  // TOUR: MOCK SCAN DEMO (no permission key — accessible to all authenticated users)
  // ==========================================================================
  {
    id: 'tour_scan_demo',
    name: 'Tour Scan Demo',
    envKey: 'FEATURE_TOUR_SCAN_DEMO',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/tour/scan-demo',
    component: lazy(() => import('../app/pages/tour/ScanDemo')),
    sidebar: { show: false },
  },

  // ==========================================================================
  // STANDALONE: ORGANIZATION DASHBOARD
  // ==========================================================================
  {
    id: 'org_dashboard',
    name: 'Overview',
    envKey: 'FEATURE_ORG_DASHBOARD',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/org-dashboard',
    mainRoute: true,
    component: lazy(() => import('../app/pages/org_dashboard')),
    sidebar: {
      show: true,
      icon: 'color-swatch',
      order: 1,
    },
    apiEndpoints: ['/org-dashboard/'],
    reduxState: ['dataOrgDashboard'],
  },

  // ==========================================================================
  // STANDALONE: DASHBOARD
  // ==========================================================================
  {
    id: 'dashboard',
    name: 'Dashboard',
    envKey: 'FEATURE_DASHBOARD',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/dashboard',
    mainRoute: true,
    component: lazy(() => import('../app/pages/dashboard/DashboardPage')),
    sidebar: {
      show: true,
      icon: 'element-plus',
      order: 2,
    },
    apiEndpoints: ['/dashboard/'],
    reduxState: ['dataDashboard'],
    permissionKey: "api:dashboard:view",
  },

  // ==========================================================================
  // MENU GROUP: SCAN
  // ==========================================================================
  {
    id: 'scan_menu',
    name: 'Scan',
    envKey: 'FEATURE_SCAN_MENU',
    type: FeatureType.MENU_GROUP,
    defaultEnabled: true,
    sidebar: {
      show: true,
      icon: 'fingerprint-scanning',
      order: 20,
    },
    children: ['scan_history', 'scan_schedule', 'scan_create'],
    permissionKey: "api:scan:view"
  },

  {
    id: 'scan_history',
    name: 'Scan History',
    envKey: 'FEATURE_SCAN_HISTORY',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/scan-history',
    component: lazy(() => import('../app/pages/scan').then(module => ({ default: module.Scan }))),
    parentId: 'scan_menu',
    sidebar: {
      show: true,
      icon: 'questionnaire-tablet',
      order: 21,
    },
    apiEndpoints: ['/scan/', '/scan/overview/'],
    reduxState: ['dataScan', 'dataChartScan'],
  },
  {
    id: 'scan_edit',
    name: 'Scan Edit',
    envKey: 'FEATURE_SCAN_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'scan_history',
    permissionKey: "api:scan:edit"
  },
  {
    id: 'scan_stop',
    name: 'Scan Stop',
    envKey: 'FEATURE_SCAN_STOP',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'scan_history',
  },
  {
    id: 'scan_delete',
    name: 'Scan Delete',
    envKey: 'FEATURE_SCAN_DELETE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'scan_history',
    permissionKey: "api:scan:delete"
  },

  {
    id: 'scan_schedule',
    name: 'Schedule',
    envKey: 'FEATURE_SCAN_SCHEDULE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/scan-schedule',
    component: lazy(() => import('../app/pages/scan/components/schedule/Schedule')),
    parentId: 'scan_menu',
    sidebar: {
      show: true,
      icon: 'watch',
      order: 22,
    },
    reduxState: ['dataScanSchedule'],
    permissionKey: "api:scan_schedule:view"
  },
  {
    id: 'scan_schedule_delete',
    name: 'Scan Schedule Delete',
    envKey: 'FEATURE_SCAN_SCHEDULE_DELETE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'scan_schedule',
    permissionKey: "api:scan_schedule:edit"
  },
  {
    id: 'scan_create',
    name: 'Create Scan',
    envKey: 'FEATURE_SCAN_CREATE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    parentId: 'scan_menu',
    route: '/create-scan',
    sidebar: {
      show: true,
      icon: 'plus-square',
      order: 23,
    },
    component: lazy(() => import('../app/pages/scan/components/CreateScan')),
    // Note: This is now a modal component, not a routed page
    // Modal is triggered via Bootstrap modal: data-bs-target='#kt_modal_create_scan'
    // No route/component needed - modal is included directly in scan history page
    permissionKey: "api:asset:scan"
  },
  {
    id: 'scannable_asset_view',
    name: 'View Scannable Assets',
    envKey: 'FEATURE_SCANNABLE_ASSET_VIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    parentId: 'scan_menu',
    // Required to load the scannable assets list in the Create Scan modal (Step 1)
    permissionKey: "api:scannable_asset:view"
  },

  {
    id: 'scan_details',
    name: 'Scan Details',
    envKey: 'FEATURE_SCAN_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/scan-details',
    component: lazy(() => import('../app/pages/scan/components/ScanDetails')),
    parentId: 'scan_menu',
  },
  {
    id: 'scan_diff',
    name: 'Scan Diff',
    envKey: 'FEATURE_SCAN_DIFF',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/scan-diff',
    component: lazy(() => import('../app/pages/scan/components/ScanDiffModal')),
    parentId: 'scan_menu',
    apiEndpoints: ['/assets/{id}/scan-diff/', '/ip/{id}/scan-diff/'],
    permissionKey: 'api:scan:view',
  },

  {
    id: 'scan_node_details',
    name: 'Scan Node Details',
    envKey: 'FEATURE_SCAN_NODE_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'scan_menu',
    permissionKey: "api:scan:view"
  },

  // ==========================================================================
  // PARENT: ATTACK SURFACE
  // ==========================================================================
  {
    id: 'attack_surface',
    name: 'Attack Surface',
    envKey: 'FEATURE_ATTACK_SURFACE',
    type: FeatureType.PARENT,
    defaultEnabled: true,
    sidebar: {
      show: true,
      icon: 'technology-4',
      order: 40,
    },
    children: [
      "attack_surface_menu",
      'attack_surface_domains',
      'attack_surface_ips',
      'attack_surface_ports',
      'attack_surface_dns_records',
      'attack_surface_ssl',
      'attack_surface_files',
      'waf_dashboard',
      'attack_surface_emails',
      'attack_surface_apps',
      'attack_surface_technologies',
      'attack_surface_leaked_data',
      'attack_surface_dns_inspect',
      'attack_surface_smtp',
    ],
    apiEndpoints: ['/attack-surface/'],
    reduxState: ['dataExposures'],
    permissionKey: "api:attack_surface:view"
  },

  {
    id: 'attack_surface_menu',
    name: 'Overview',
    envKey: 'FEATURE_ATTACK_SURFACE_menu',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: false,
    route: '/attack-surface',
    component: lazy(() => import('../app/pages/exposures')),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'color-swatch',
      order: 41,
    },
    permissionKey: "api:attack_surface:view"
  },
  {
    id: 'attack_surface_domains',
    name: 'Domain',
    envKey: 'FEATURE_ATTACK_SURFACE_DOMAINS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/attack-surface-explore/Domains',
    component: lazy(() => import('../app/pages/exposures/Domains/Domains')),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'geolocation',
      order: 42,
    },
    alternativeAccess: {
      description: 'Dashboard → Assets at Risk widget',
      routes: ['/dashboard'],
    },
    apiEndpoints: ['/assets/'],
    reduxState: ['dataAsset', 'filterOption.assets'],
    permissionKey: "api:host:view"
  },

  {
    id: 'domains_bulk_owner',
    name: 'Domains Bulk Owner',
    envKey: 'FEATURE_DOMAINS_BULK_OWNER',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'attack_surface_domains',
    permissionKey: "api:host:edit"
  },
  {
    id: 'ips_bulk_owner',
    name: 'IPs Bulk Owner',
    envKey: 'FEATURE_IPS_BULK_OWNER',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'attack_surface_ips',
    permissionKey: "api:ip:edit"
  },

  {
    id: 'attack_surface_ips',
    name: 'IP',
    envKey: 'FEATURE_ATTACK_SURFACE_IPS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/attack-surface-explore/IP',
    component: lazy(() => import('../app/pages/exposures/IPs/IPs')),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'router',
      order: 43,
    },
    apiEndpoints: ['/ip/'],
    reduxState: ['dataIp', 'filterOption.ip'],
    permissionKey: "api:ip:view"
  },

  {
    id: 'attack_surface_ports',
    name: 'Port',
    envKey: 'FEATURE_ATTACK_SURFACE_PORTS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/attack-surface-explore/Ports',
    component: lazy(() => import('../app/pages/exposures/Ports/Ports')),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'route',
      order: 44,
    },
    apiEndpoints: ['/port/'],
    reduxState: ['dataPorts', 'filterOption.port'],
    permissionKey: "api:port:view"
  },

  {
    id: 'attack_surface_dns_records',
    name: 'DNS Record',
    envKey: 'FEATURE_ATTACK_SURFACE_DNS_RECORDS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/attack-surface-explore/DNS Records',
    component: lazy(() => import('../app/pages/exposures/DNS/DNS')),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'category',
      order: 45,
    },
    apiEndpoints: ['/dns-record/'],
    reduxState: ['dataDnsRecords', 'filterOption.dnsRecord'],
    permissionKey: "api:dns_record:view"
  },

  {
    id: 'attack_surface_ssl',
    name: 'SSL',
    envKey: 'FEATURE_ATTACK_SURFACE_SSL',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.MULTI_ACCESS,
    defaultEnabled: true,
    route: '/attack-surface-explore/SSL',
    component: lazy(() => import('../app/pages/exposures/SSL/SSL').then(module => ({ default: module.SSLExposures }))),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'shield-tick',
      order: 46,
    },
    alternativeAccess: {
      description: 'Dashboard → Expiring SSL widget',
      routes: ['/dashboard'],
    },
    apiEndpoints: ['/ssl/'],
    reduxState: ['dataSSL', 'filterOption.ssl'],
    permissionKey: "api:ssl:view"
  },

  {
    id: 'attack_surface_emails',
    name: 'Emails',
    envKey: 'FEATURE_ATTACK_SURFACE_EMAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/attack-surface-explore/Email Address',
    component: lazy(() => import('../app/pages/exposures/Email/Email')),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'mailAddress',
      order: 47,
    },
    apiEndpoints: ['/email/'],
    reduxState: ['dataEmails', 'filterOption.email'],
    permissionKey: "api:email:view"
  },

  {
    id: 'attack_surface_apps',
    name: 'Application',
    envKey: 'FEATURE_ATTACK_SURFACE_APPS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: false,
    route: '/attack-surface-explore/Applications',
    component: lazy(() => import('../app/pages/exposures/App/App')),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'Smartphone',
      order: 48,
    },
  },

  {
    id: 'attack_surface_technologies',
    name: 'Technologies',
    envKey: 'FEATURE_ATTACK_SURFACE_TECHNOLOGIES',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: false,
    route: '/attack-surface-explore/Technologies',
    component: lazy(() => import('../app/pages/exposures/Tech/Tech')),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'technology-2',
      order: 49,
    },
  },

  {
    id: 'attack_surface_leaked_data',
    name: 'Leaked Data',
    envKey: 'FEATURE_ATTACK_SURFACE_LEAKED_DATA',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/attack-surface-explore/Leaked Data',
    component: lazy(() => import('../app/pages/exposures/Leaked/Leaked')),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'security-check',
      order: 50,
    },
    apiEndpoints: ['/leaked-data/'],
    reduxState: ['dataLeaked'],
    permissionKey: "api:leaked_data:view"
  },

  {
    id: 'attack_surface_dns_inspect',
    name: 'DNS Server',
    envKey: 'FEATURE_ATTACK_SURFACE_DNS_INSPECT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/attack-surface/dns',
    component: lazy(() => import('../app/pages/dnsInspect').then(module => ({ default: module.DNSInspect }))),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'magnifier',
      order: 51.1,
    },
    apiEndpoints: ['/dns/'],
    reduxState: ['dataDns'],
    permissionKey: "api:dns:view"
  },

  {
    id: 'attack_surface_smtp',
    name: 'Mail Server',
    envKey: 'FEATURE_ATTACK_SURFACE_SMTP',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/attack-surface/smtp',
    component: lazy(() => import('../app/pages/smtp').then(module => ({ default: module.SMTP }))),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'sms',
      order: 51.2,
    },
    apiEndpoints: ['/smtp/'],
    reduxState: ['dataSmtp'],
    permissionKey: "api:smtp:view"
  },

  // Supporting routes for attack surface
  {
    id: 'attack_surface_asset_details',
    name: 'Asset Details',
    envKey: 'FEATURE_ATTACK_SURFACE_ASSET_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/asset-details',
    component: lazy(() => import('../app/pages/exposures/Domains/details/AssetDetails')),
    parentId: 'attack_surface',
    dependencies: ['attack_surface_domains'],
  },

  // ==========================================================================
  // PLATFORM — the superuser console: plans, subscriptions, billing, settings and
  // two-factor administration for every organization. Superuser-only.
  // ==========================================================================
  {
    id: 'platform_menu',
    name: 'Platform',
    envKey: 'FEATURE_PLATFORM',
    type: FeatureType.MENU_GROUP,
    defaultEnabled: true,
    superuserOnly: true,
    sidebar: { show: true, icon: 'category', order: 120 },
    apiEndpoints: ['/platform/'],
  },
  {
    id: 'platform_plans',
    name: 'Plans',
    envKey: 'FEATURE_PLATFORM_PLANS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    superuserOnly: true,
    route: '/platform/plans',
    component: lazy(() => import('../app/pages/platform/plans/PlansPage')),
    parentId: 'platform_menu',
    sidebar: { show: true, order: 1 },
    apiEndpoints: ['/platform/plans/', '/platform/plan-categories/', '/platform/features/'],
  },
  {
    id: 'platform_plan_form',
    name: 'Plan',
    envKey: 'FEATURE_PLATFORM_PLANS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    superuserOnly: true,
    route: '/platform/plans/edit',
    component: lazy(() => import('../app/pages/platform/plans/PlanFormPage')),
    parentId: 'platform_menu',
    dependencies: ['platform_plans'],
  },
  {
    id: 'platform_plan_catalog',
    name: 'Plan catalog',
    envKey: 'FEATURE_PLATFORM_PLANS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    superuserOnly: true,
    route: '/platform/plans/catalog',
    component: lazy(() => import('../app/pages/platform/plans/CatalogPage')),
    parentId: 'platform_menu',
    dependencies: ['platform_plans'],
  },
  {
    id: 'platform_subscriptions',
    name: 'Subscriptions',
    envKey: 'FEATURE_PLATFORM_SUBSCRIPTIONS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    superuserOnly: true,
    route: '/platform/subscriptions',
    component: lazy(() => import('../app/pages/platform/subscriptions/SubscriptionsPage')),
    parentId: 'platform_menu',
    sidebar: { show: true, order: 2 },
    apiEndpoints: ['/platform/subscriptions/', '/platform/organizations/'],
  },
  {
    id: 'platform_subscription_detail',
    name: 'Subscription',
    envKey: 'FEATURE_PLATFORM_SUBSCRIPTIONS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    superuserOnly: true,
    route: '/platform/subscription',
    component: lazy(() => import('../app/pages/platform/subscriptions/SubscriptionDetailPage')),
    parentId: 'platform_menu',
    dependencies: ['platform_subscriptions'],
  },
  {
    id: 'platform_billing',
    name: 'Billing',
    envKey: 'FEATURE_PLATFORM_BILLING',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    superuserOnly: true,
    route: '/platform/billing',
    component: lazy(() => import('../app/pages/platform/billing/BillingPage')),
    parentId: 'platform_menu',
    sidebar: { show: true, order: 3 },
    apiEndpoints: ['/platform/invoices/', '/platform/payments/'],
  },
  {
    id: 'platform_settings',
    name: 'Settings',
    envKey: 'FEATURE_PLATFORM_SETTINGS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    superuserOnly: true,
    route: '/platform/settings',
    component: lazy(() => import('../app/pages/platform/settings/SettingsPage')),
    parentId: 'platform_menu',
    sidebar: { show: true, order: 4 },
    apiEndpoints: ['/platform/settings/', '/platform/setting-choices/'],
  },
  {
    id: 'platform_setting_choices',
    name: 'Setting choices',
    envKey: 'FEATURE_PLATFORM_SETTINGS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    superuserOnly: true,
    route: '/platform/settings/choices',
    component: lazy(() => import('../app/pages/platform/settings/ChoicesPage')),
    parentId: 'platform_menu',
    dependencies: ['platform_settings'],
  },
  {
    id: 'platform_two_factor',
    name: 'Two-factor authentication',
    envKey: 'FEATURE_PLATFORM_TWO_FACTOR',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    superuserOnly: true,
    route: '/platform/two-factor',
    component: lazy(() => import('../app/pages/platform/twoFactor/TwoFactorPage')),
    parentId: 'platform_menu',
    sidebar: { show: true, order: 5 },
    apiEndpoints: ['/platform/two-factor/', '/platform/users/'],
  },

  // ==========================================================================
  // WAF ASSESSMENTS — live. `GET /waf-assessments/` (+ `/filter/`, `/{id}/`) and
  // the `waf` block of the asset-insights payload. Gated by its own scope,
  // `api:waf_assessment:view`, which is NOT implied by the ssl/host/ip scopes and
  // is absent from the Free plan: expect many users to lack it, and a 403 renders
  // a "no access" state in place.
  // ==========================================================================
  {
    id: 'waf_dashboard',
    name: 'Waf',
    envKey: 'FEATURE_WAF_DASHBOARD',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.MULTI_ACCESS,
    defaultEnabled: true,
    route: '/waf',
    component: lazy(() => import('../app/pages/waf').then((module) => ({ default: module.Waf }))),
    // Lives under Attack surface, beside SSL — the firewall in front of an asset
    // is part of how exposed it is.
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'shield-tick',
      order: 46.5,
    },
    alternativeAccess: {
      description: 'Domain and IP detail pages → WAF posture panel',
      routes: ['/asset-details', '/ip-details'],
    },
    apiEndpoints: ['/waf-assessments/'],
    permissionKey: 'api:waf_assessment:view',
  },

  {
    id: 'waf_details',
    name: 'WAF Details',
    envKey: 'FEATURE_WAF_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/waf-details',
    component: lazy(() => import('../app/pages/waf/WafDetails').then((module) => ({ default: module.WafDetails }))),
    parentId: 'attack_surface',
    dependencies: ['waf_dashboard'],
  },

  // ==========================================================================
  // LOAD TESTS ("DoS") — `GET /load-tests/` (+ `/filter/`, `/{id}/`) and the
  // `load_test` block of the asset-insights payload. Own scope
  // `api:load_test:view`; a 403 renders a "no access" state in place.
  // ==========================================================================
  {
    id: 'dos_dashboard',
    name: 'Dos',
    envKey: 'FEATURE_DOS_DASHBOARD',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/dos',
    component: lazy(() => import('../app/pages/loadTest').then((module) => ({ default: module.LoadTests }))),
    sidebar: {
      show: true,
      icon: 'shield-off',
      order: 61.5,
    },
    alternativeAccess: {
      description: 'Domain and IP detail pages → Load test panel',
      routes: ['/asset-details', '/ip-details'],
    },
    apiEndpoints: ['/load-tests/'],
    permissionKey: 'api:load_test:view',
  },

  {
    id: 'dos_details',
    name: 'Load test details',
    envKey: 'FEATURE_DOS_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/dos-details',
    component: lazy(() => import('../app/pages/loadTest/LoadTestDetails').then((module) => ({ default: module.LoadTestDetails }))),
    dependencies: ['dos_dashboard'],
  },

  // Uploaded files (APK, archive, spreadsheet, text, image tar) — they carry
  // findings now, so they get the same list + detail pair hosts and addresses do.
  {
    id: 'attack_surface_files',
    name: 'Files',
    envKey: 'FEATURE_ATTACK_SURFACE_FILES',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/attack-surface-explore/Files',
    component: lazy(() => import('../app/pages/exposures/Files/Files').then(module => ({ default: module.FilesExposures }))),
    parentId: 'attack_surface',
    sidebar: {
      show: true,
      icon: 'some-files',
      order: 47,
    },
    apiEndpoints: ['/files/'],
    permissionKey: 'api:uploaded_file:view',
  },

  {
    id: 'attack_surface_file_details',
    name: 'File Details',
    envKey: 'FEATURE_ATTACK_SURFACE_FILE_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/file-details',
    component: lazy(() => import('../app/pages/exposures/Files/FileDetails')),
    parentId: 'attack_surface',
    dependencies: ['attack_surface_files'],
  },

  {
    id: 'attack_surface_ssl_details',
    name: 'SSL Details',
    envKey: 'FEATURE_ATTACK_SURFACE_SSL_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/ssl-details',
    component: lazy(() => import('../app/pages/exposures/SSL/details/SSLDetails')),
    parentId: 'attack_surface',
    dependencies: ['attack_surface_ssl'],
  },

  // App Details feature removed - component does not exist in current version
  // {
  //   id: 'attack_surface_app_details',
  //   name: 'App Details',
  //   envKey: 'FEATURE_ATTACK_SURFACE_APP_DETAILS',
  //   type: FeatureType.CHILD,
  //   orphanStatus: OrphanStatus.INDEPENDENT,
  //   defaultEnabled: true,
  //   route: '/attack-surface-explore/Applications/details',
  //   component: lazy(() => import('../app/pages/exposures/App/Details')),
  //   parentId: 'attack_surface',
  // },

  {
    id: 'attack_surface_ip_details',
    name: 'IP Details',
    envKey: 'FEATURE_ATTACK_SURFACE_IP_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/ip-details',
    component: lazy(() => import('../app/pages/exposures/IPs/IpDetails')),
    parentId: 'attack_surface',
    dependencies: ['attack_surface_ips'],
  },

  // ==========================================================================
  // ASSET INSIGHTS: capabilities of the two detail pages
  // ==========================================================================
  {
    id: 'asset_insights_tech_exposure',
    name: 'Asset Technologies Exposure',
    envKey: 'FEATURE_ASSET_INSIGHTS_TECH_EXPOSURE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'attack_surface',
  },

  {
    id: 'asset_insights_actions',
    name: 'Asset Insights Actions',
    envKey: 'FEATURE_ASSET_INSIGHTS_ACTIONS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'attack_surface',
  },

  {
    id: 'asset_insights_business_value',
    name: 'Business Value',
    envKey: 'FEATURE_ASSET_INSIGHTS_BUSINESS_VALUE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'attack_surface',
  },

  // ==========================================================================
  // ORPHANED FEATURES: TARGET CREATE & VERIFY (No page, used in modals)
  // ==========================================================================
  {
    id: 'target_create',
    name: 'Create Target',
    envKey: 'FEATURE_TARGET_CREAT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    permissionKey: "api:asset:add"
  },

  {
    id: 'target_verify',
    name: 'Verify Target',
    envKey: 'FEATURE_TARGET_VERIFY',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    permissionKey: "api:asset:add"
  },

  // ==========================================================================
  // STANDALONE: VULNERABILITIES
  // ==========================================================================
  {
    id: 'vulnerabilities',
    name: 'Vulnerabilities',
    envKey: 'FEATURE_VULNERABILITIES',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/vulnerabilities',
    component: lazy(() => import('../app/pages/vulnerabilities').then(module => ({ default: module.Vulnerabilities }))),
    sidebar: {
      show: true,
      icon: 'Bug',
      order: 60,
    },
    alternativeAccess: {
      description: 'Dashboard → Recent Vulnerabilities widget',
      routes: ['/dashboard'],
    },
    apiEndpoints: ['/vulnerabilities/', '/vulnerabilities/filter/'],
    reduxState: ['dataVulnerability', 'dataVulDetails', 'dataPoC'],
    permissionKey: "api:vulnerability:view"
  },

  {
    id: 'vulnerability_details',
    name: 'Vulnerability Details',
    envKey: 'FEATURE_VULNERABILITY_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/vulnerability-details',
    component: lazy(() => import('../app/pages/vulnerabilities/components/VulnerabilityDetails')),
    parentId: 'vulnerabilities',
  },
  {
    id: 'vulnerability_comments',
    name: 'Vulnerability Comments',
    envKey: 'FEATURE_VULNERABILITY_COMMENTS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'vulnerabilities',
    permissionKey: "api:vulnerability:edit"
  },

  // ==========================================================================
  // STANDALONE: COMPLIANCE
  // ==========================================================================
  // Benchmark controls evaluated against hosts. A PASS is a row, not an
  // absence — the score cannot be stated without counting passes — so this
  // list is never defaulted to failures.
  {
    id: 'compliance',
    name: 'Compliance',
    envKey: 'FEATURE_COMPLIANCE',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/compliance',
    component: lazy(() => import('../app/pages/compliance').then(module => ({ default: module.Compliance }))),
    sidebar: {
      show: true,
      icon: 'ClipboardCheck',
      order: 61,
    },
    apiEndpoints: [
      '/compliance/findings/',
      '/compliance/findings/filter/',
      '/compliance/stats/',
      '/compliance/benchmarks/',
    ],
    // NO permissionKey on purpose: the engine ANDs the env flag with the
    // permission, so gating the PAGE on `api:compliance:view` makes it
    // unreachable — no sidebar entry and no route — for every org until the
    // backend ships that scope, including while the feature is being built.
    // The flag governs visibility; `api:compliance:view` is still enforced by
    // the backend on every GET, and a 403 renders in place as <NotPermission/>
    // (see the `/compliance/` entries in SetupAxios's exemption lists).
    // Add the key back once the scope is granted in production roles.
  },
  {
    id: 'compliance_details',
    name: 'Compliance Finding',
    envKey: 'FEATURE_COMPLIANCE_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/compliance-details',
    component: lazy(() => import('../app/pages/compliance/components/ComplianceDetails')),
    parentId: 'compliance',
  },
  {
    // Gates the WRITES only: setting/clearing an exception and every comment
    // write. Held without it, the controls render disabled rather than hidden,
    // so the gap is legible to a read-only role.
    id: 'compliance_edit',
    name: 'Compliance Exceptions & Comments',
    envKey: 'FEATURE_COMPLIANCE_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'compliance',
    permissionKey: 'api:compliance:edit',
  },

  // ==========================================================================
  // STANDALONE: ISSUES
  // ==========================================================================
  // The remediation-tracking layer over findings: one issue IS one finding
  // (one vulnerability on one asset at one location), served by
  // `/vulnerabilities/*`.
  {
    id: 'issues',
    name: 'Issues',
    envKey: 'FEATURE_ISSUES',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/issues',
    component: lazy(() => import('../app/pages/issues').then(module => ({ default: module.Issues }))),
    sidebar: {
      show: true,
      icon: 'ClipboardList',
      order: 62,
    },
    apiEndpoints: [
      '/vulnerabilities/',
      '/vulnerabilities/filter/',
      '/vulnerabilities/workflow-config/',
    ],
    // NO permissionKey on purpose, for the same reason as `compliance` below
    // it: the engine ANDs the env flag with the permission, so gating the PAGE
    // on `api:issue` makes it unreachable — no sidebar entry and no route —
    // for every org until the backend grants that scope, including while the
    // feature is being built. The flag governs visibility; `api:issue` is
    // still enforced by the backend on every request, and a 403 renders in
    // place as <NotPermission/> (the Issues API client marks its requests `OWNS_ERRORS`).
  },
  {
    id: 'issues_details',
    name: 'Issue',
    envKey: 'FEATURE_ISSUES_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/issue-details',
    component: lazy(() => import('../app/pages/issues/IssueDetailsPage')),
    parentId: 'issues',
  },
  {
    // The SLA policy and the ownership note. Unlike the board, this one DOES
    // carry its permission key: the admin surface has no read-only mode, and
    // the entry point is hidden rather than shown-then-refused. The screen
    // still treats its own 403 as a real runtime outcome, because which roles
    // hold which verb is the permission service's decision, not this API's.
    id: 'issues_admin',
    name: 'Issue Settings',
    envKey: 'FEATURE_ISSUES_ADMIN',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/issue-settings',
    component: lazy(() => import('../app/pages/issues/settings').then(module => ({ default: module.IssueSettings }))),
    parentId: 'issues',
    permissionKey: 'api:issue_admin',
  },

  // ==========================================================================
  // PARENT: WORKFLOW
  // ==========================================================================
  {
    id: 'workflow',
    name: 'Workflow',
    envKey: 'FEATURE_WORKFLOW',
    type: FeatureType.PARENT,
    defaultEnabled: true,
    sidebar: {
      show: true,
      icon: 'data',
      order: 70,
    },
    children: ['workflow_list', 'workflow_library', 'component_settings'],
    apiEndpoints: ['/workflow/', '/workflow/node-types'],
    reduxState: ['dataWorkflow', 'dataScanDAG', 'nodeDetailsScanDag'],
    permissionKey: "api:workflow:view"
  },
  {
    id: 'workflow_list',
    name: 'Workflows',
    envKey: 'FEATURE_WORKFLOW_LIST',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/workFlow',
    component: lazy(() => import('../app/pages/workFlow').then(module => ({ default: module.WorkFlow }))),
    sidebar: {
      show: true,
      icon: 'data',
    },
    parentId: 'workflow',
    permissionKey: "api:workflow:view"
  },

  {
    id: 'component_settings',
    name: 'Component Settings',
    envKey: 'FEATURE_COMPONENT_SETTINGS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/component-settings',
    component: lazy(() => import('../app/pages/componentSettings')),
    sidebar: {
      show: true,
      icon: 'gear',
    },
    parentId: 'workflow',
    apiEndpoints: ['/workflow/component-configs'],
    permissionKey: "api:component_config:view"
  },
  {
    id: 'component_settings_edit',
    name: 'Edit Component Settings',
    envKey: 'FEATURE_COMPONENT_SETTINGS_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    parentId: 'component_settings',
    permissionKey: "api:component_config:edit"
  },

  {
    id: 'workflow_delete',
    name: 'Delete Workflow',
    envKey: 'FEATURE_WORKFLOW_DELETE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    parentId: 'workflow',
    permissionKey: "api:workflow:delete"
  },
  {
    id: 'workflow_view',
    name: 'View Workflow',
    envKey: 'FEATURE_WORKFLOW_VIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/workFlow-edit',
    component: lazy(() => import('../app/pages/workFlow/components/CreateDAG')),
    parentId: 'workflow',
  },
  {
    id: 'workflow_versions',
    name: 'Workflow Versions',
    envKey: 'FEATURE_WORKFLOW_VERSIONS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/workFlow-versions',
    component: lazy(() => import('../app/pages/workFlow/components/versions')),
    parentId: 'workflow',
    permissionKey: "api:workflow:view"
  },
  {
    id: 'workflow_create',
    name: 'Create Workflow',
    envKey: 'FEATURE_WORKFLOW_CREATE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    // route: '/workFlow-edit',
    // component: lazy(() => import('../app/pages/workFlow/components/CreateDAG')),
    parentId: 'workflow',
    permissionKey: "api:workflow:add"
  },

  {
    id: 'workflow_edit',
    name: 'Edit Workflow',
    envKey: 'FEATURE_WORKFLOW_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    // route: '/workFlow-edit',
    // component: lazy(() => import('../app/pages/workFlow/components/CreateDAG')),
    parentId: 'workflow',
    permissionKey: "api:workflow:edit"

  },

  {
    id: 'workflow_node_details',
    name: 'Workflow Node Details',
    envKey: 'FEATURE_WORKFLOW_NODE_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/workflow/node-types',
    component: lazy(() => import('../app/pages/workFlow/components/NodeDetails')),
    parentId: 'workflow',
  },
  // ==========================================================================
  // ORPHANED FEATURES: Workflow library
  // ==========================================================================
  {
    id: 'workflow_library',
    name: 'Workflow Library',
    envKey: 'FEATURE_WORKFLOW_LIBRARY_VIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/workFlow-library',
    component: lazy(() => import('../app/pages/workFlow/components/library')),
    sidebar: {
      show: true,
      icon: 'book',
    },
    parentId: 'workflow',
  },
  {
    id: 'workflow_library_preview',
    name: 'View Workflow Library Preview',
    envKey: 'FEATURE_WORKFLOW_LIBRARY_PREVIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/workFlow-library-preview',
    component: lazy(() => import('../app/pages/workFlow/components/library/preview/Preview')),
    parentId: 'workflow',
  },
  // ==========================================================================
  // STANDALONE: INTEGRATIONS
  // ==========================================================================
  {
    id: 'integrations',
    name: 'Integrations',
    envKey: 'FEATURE_INTEGRATIONS',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/integrations',
    component: lazy(() => import('../app/pages/integrations/Index')),
    sidebar: {
      show: true,
      icon: 'gear',
      order: 80,
    },
    apiEndpoints: ['/vul-logger/'],
    reduxState: ['dataIntegrations'],
    permissionKey: "api:vul_logger:view"
  },
  {
    id: 'integration_create',
    name: 'Create Integration',
    envKey: 'FEATURE_INTEGRATION_CREATE',
    type: FeatureType.CHILD,
    defaultEnabled: true,
    parentId: 'integrations',
    permissionKey: "api:vul_logger:add"
  },
  {
    id: 'integration_edit',
    name: 'Edit Integration',
    envKey: 'FEATURE_INTEGRATION_EDIT',
    type: FeatureType.CHILD,
    defaultEnabled: true,
    parentId: 'integrations',
    permissionKey: "api:vul_logger:edit"
  },
  {
    id: 'integration_history',
    name: 'Integration History',
    envKey: 'FEATURE_INTEGRATION_HISTORY',
    type: FeatureType.CHILD,
    defaultEnabled: true,
    parentId: 'integrations',
  },
  {
    id: 'integration_status',
    name: 'Integration Status',
    envKey: 'FEATURE_INTEGRATION_STATUS',
    type: FeatureType.CHILD,
    defaultEnabled: true,
    parentId: 'integrations',
  },
  {
    id: 'integration_delete',
    name: 'Delete Integration',
    envKey: 'FEATURE_INTEGRATION_DELETE',
    type: FeatureType.CHILD,
    defaultEnabled: true,
    parentId: 'integrations',
    permissionKey: "api:vul_logger:delete"
  },

  // ==========================================================================
  // MENU GROUP: REPORTS
  // ==========================================================================
  {
    id: 'reports_menu',
    name: 'Reports',
    envKey: 'FEATURE_REPORTS_MENU',
    type: FeatureType.MENU_GROUP,
    defaultEnabled: true,
    sidebar: {
      show: true,
      icon: 'some-files',
      order: 90,
    },
    children: ['reports_history', 'reports_create', 'reports_templates'],
    permissionKey: "api:report:view"
  },

  {
    id: 'reports_history',
    name: 'Report History',
    envKey: 'FEATURE_REPORTS_HISTORY',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/report/history',
    component: lazy(() => import('../app/pages/reports')),
    parentId: 'reports_menu',
    sidebar: {
      show: true,
      icon: 'questionnaire-tablet',
      order: 91,
    },
    reduxState: ['dataReportHistory', 'dataReportScan'],
    permissionKey: "api:report:view"
  },

  {
    id: 'reports_create',
    name: 'Create Report',
    envKey: 'FEATURE_REPORTS_CREATE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/report/create',
    component: lazy(() => import('../app/pages/reports/create_report')),
    parentId: 'reports_menu',
    dependencies: ['reports_history'],  // Requires reports_history to be meaningful (can't create reports if you can't view them)
    sidebar: {
      show: true,
      icon: 'add-notepad',
      order: 92,
    },
    permissionKey: "api:report:add"
  },

  {
    id: 'reports_edit',
    name: 'Edit Report',
    envKey: 'FEATURE_REPORTS_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'reports_history',
    permissionKey: "api:report:edit"
  },

  {
    id: 'reports_delete',
    name: 'Delete Report',
    envKey: 'FEATURE_REPORTS_DELETE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'reports_history',
    permissionKey: "api:report:delete"
  },

  {
    id: 'reports_templates',
    name: 'Templates',
    envKey: 'FEATURE_REPORTS_TEMPLATES',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/report/templates',
    component: lazy(() => import('../app/pages/reports/TemplateList')),
    parentId: 'reports_menu',
    sidebar: {
      show: true,
      icon: 'devices-2',
      order: 93,
    },
    permissionKey: "api:report:add"
  },

  {
    id: 'reports_template_generator',
    name: 'Template Generator',
    envKey: 'FEATURE_REPORTS_TEMPLATE_GENERATOR',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.INDEPENDENT,
    defaultEnabled: true,
    route: '/report/template-generator',
    component: lazy(() => import('../app/pages/reports/template_generator/index')),
    parentId: 'reports_menu',
    permissionKey: "api:report:add"
  },

  // ==========================================================================
  // STANDALONE: TICKETS
  // ==========================================================================
  {
    id: 'tickets',
    name: 'Tickets',
    envKey: 'FEATURE_TICKETS',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/ticket',
    component: lazy(() => import('../app/pages/ticket/Index')),
    permissionKey: "api:ticket:view"
  },
  {
    id: 'tickets_create',
    name: 'Create Tickets',
    envKey: 'FEATURE_TICKET_CREATE',
    defaultEnabled: true,
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    parentId: 'tickets',
    permissionKey: "api:ticket:add",
  },

  {
    id: 'ticket_details',
    name: 'Ticket Details',
    envKey: 'FEATURE_TICKET_DETAILS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/ticket-details',
    component: lazy(() => import('../app/pages/ticket/TicketDetails')),
    parentId: 'tickets',
  },

  // ==========================================================================
  // MENU GROUP: BILLING & SUBSCRIPTION
  // ==========================================================================
  {
    id: 'subscription_menu',
    name: 'My Subscription',
    envKey: 'FEATURE_SUBSCRIPTION_MENU',
    type: FeatureType.MENU_GROUP,
    defaultEnabled: true,
    children: ['price_plan', 'services', 'invoices', 'payment_history'],
  },

  {
    id: 'price_plan',
    name: 'Price Plan',
    envKey: 'FEATURE_PRICE_PLAN',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/price-plan',
    component: lazy(() => import('../app/pages/price_plan/PricePlan')),
    parentId: 'subscription_menu',
  },

  {
    id: 'payment_history',
    name: 'Payment History',
    envKey: 'FEATURE_PAYMENT_HISTORY',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/payment-history',
    component: lazy(() => import('../app/pages/price_plan/History')),
    parentId: 'subscription_menu',
  },

  {
    id: 'invoices',
    name: 'Invoices',
    envKey: 'FEATURE_INVOICES',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/Invoices',
    component: lazy(() => import('../app/pages/price_plan/Invoices')),
    parentId: 'subscription_menu',
  },

  {
    id: 'services',
    name: 'Services',
    envKey: 'FEATURE_SERVICES',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    route: '/Services',
    component: lazy(() => import('../app/pages/services/Services')),
    parentId: 'subscription_menu',
  },

  {
    id: 'payment_verify',
    name: 'Payment Verify',
    envKey: 'FEATURE_PAYMENT_VERIFY',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/payment/verify/*',
    component: lazy(() => import('../app/pages/price_plan/Verify')),
  },

  {
    id: 'payment_verify_free',
    name: 'Payment Verify Free',
    envKey: 'FEATURE_PAYMENT_VERIFY_FREE',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/payment/verify_success/*',
    component: lazy(() => import('../app/pages/price_plan/VerifyFree')),
  },

  // ==========================================================================
  // AI FEATURES
  // ==========================================================================
  {
    id: 'vulnerabilities_chat',
    name: 'Messages',
    envKey: 'FEATURE_VULNERABILITIES_CHAT',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    dependencies: ['vulnerabilities'],
    permissionKey: "api:chatbot:view"
  },
  {
    id: 'vulnerabilities_chat_create',
    name: 'Create And Send Messages',
    envKey: 'FEATURE_VULNERABILITIES_CHAT_CREATE',
    type: FeatureType.STANDALONE,

    defaultEnabled: true,
    dependencies: ['vulnerabilities', "vulnerabilities_chat"],
    permissionKey: "api:chatbot:add"
  },
  {
    id: 'vulnerabilities_chat_feedback',
    name: 'Feedback Messages',
    envKey: 'FEATURE_VULNERABILITIES_CHAT_FEEDBACK',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    dependencies: ['vulnerabilities', "vulnerabilities_chat"],
    permissionKey: "api:chatbot:edit"
  },
  {
    id: 'vulnerabilities_chat_patch',
    name: 'Patch Session',
    envKey: 'FEATURE_VULNERABILITIES_CHAT_PATCH',
    type: FeatureType.STANDALONE,
    // Off by default — experimental Patcher. Flip REACT_APP_FEATURE_VULNERABILITIES_CHAT_PATCH=true to enable.
    defaultEnabled: true,
    dependencies: ['vulnerabilities', 'vulnerabilities_chat', 'vulnerabilities_chat_create'],
    permissionKey: 'api:chatbot:add',
  },
  {
    id: 'chat_history',
    name: 'Chat History',
    envKey: 'FEATURE_CHAT_HISTORY',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/chat-bot',
    component: lazy(() => import('../app/pages/chat-history/ChatHistoryPage')),
    dependencies: ['vulnerabilities', "vulnerabilities_chat"],
    sidebar: {
      show: true,
      icon: 'illustrator',
      order: 65,
    },
    permissionKey: "api:chatbot:view"
  },

  // ==========================================================================
  // ORGANIZATION MANAGEMENT (Not shown in sidebar, accessed via header)
  // ==========================================================================
  {
    id: 'organization',
    name: 'Organization',
    envKey: 'FEATURE_ORGANIZATION',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/organization',
    component: lazy(() => import('../app/modules/accounts/components/organization')),
    permissionKey: "api:organization:view"
  },
  {
    id: 'select_organization',
    name: 'Select Organization',
    envKey: 'FEATURE_SELECT_ORGANIZATION',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    route: '/select-organization',
    component: lazy(() => import('../app/modules/accounts/components/organization/selectOrg/SelectOrg')),
  },

  // ==========================================================================
  // ORGANIZATION MANAGEMENT FEATURES
  // ==========================================================================
  {
    id: 'organization_management',
    name: 'Organization Management',
    envKey: 'FEATURE_ORGANIZATION_MANAGEMENT',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    dependencies: ['organization'],
    permissionKey: "api:organization:view"
  },

  {
    id: 'organization_tree_view',
    name: 'View Organization Tree',
    envKey: 'FEATURE_ORGANIZATION_TREE_VIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_management',
    dependencies: ['organization'],
  },

  {
    id: 'organization_edit',
    name: 'Update Organization',
    envKey: 'FEATURE_ORGANIZATION_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_management',
    dependencies: ['organization'],
    permissionKey: "api:organization:edit"
  },

  {
    id: 'organization_delete',
    name: 'Remove Organizations',
    envKey: 'FEATURE_ORGANIZATION_DELETE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_management',
    dependencies: ['organization'],
    permissionKey: "api:organization:delete"
  },

  // Organization Member Management
  {
    id: 'organization_membership',
    name: 'Organization Member Management',
    envKey: 'FEATURE_ORGANIZATION_MEMBERSHIP',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    dependencies: ['organization'],
    permissionKey: "api:organization_membership:view"
  },

  {
    id: 'organization_membership_view',
    name: 'View Organization Members',
    envKey: 'FEATURE_ORGANIZATION_MEMBERSHIP_VIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_membership',
    dependencies: ['organization'],
    permissionKey: "api:organization_membership:view"
  },

  {
    id: 'organization_membership_add',
    name: 'Add Members to Organization',
    envKey: 'FEATURE_ORGANIZATION_MEMBERSHIP_ADD',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_membership',
    dependencies: ['organization'],
    permissionKey: "api:organization_membership:add"
  },

  {
    id: 'organization_membership_create',
    name: 'Create a New User Member',
    envKey: 'FEATURE_ORGANIZATION_MEMBERSHIP_CREATE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_membership',
    dependencies: ['organization'],
    permissionKey: "api:organization_membership:create"
  },

  {
    id: 'organization_membership_edit',
    name: 'Update Member Roles',
    envKey: 'FEATURE_ORGANIZATION_MEMBERSHIP_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_membership',
    dependencies: ['organization'],
    permissionKey: "api:organization_membership:edit"
  },

  // Organization Role Management
  {
    id: 'organization_role',
    name: 'Organization Role Management',
    envKey: 'FEATURE_ORGANIZATION_ROLE',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    dependencies: ['organization'],
    permissionKey: "api:organization_role:view"
  },

  {
    id: 'organization_role_view',
    name: 'View Organization Roles',
    envKey: 'FEATURE_ORGANIZATION_ROLE_VIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_role',
    dependencies: ['organization'],
    permissionKey: "api:organization_role:view"
  },

  {
    id: 'organization_role_add',
    name: 'Create New Roles',
    envKey: 'FEATURE_ORGANIZATION_ROLE_ADD',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_role',
    dependencies: ['organization'],
    permissionKey: "api:organization_role:add"
  },

  {
    id: 'organization_role_edit',
    name: 'Update Role Definitions',
    envKey: 'FEATURE_ORGANIZATION_ROLE_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_role',
    dependencies: ['organization'],
    permissionKey: "api:organization_role:edit"
  },

  {
    id: 'organization_role_delete',
    name: 'Remove Roles',
    envKey: 'FEATURE_ORGANIZATION_ROLE_DELETE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_role',
    dependencies: ['organization'],
    permissionKey: "api:organization_role:delete"
  },

  {
    id: 'organization_role_members',
    name: 'View Role Members',
    envKey: 'FEATURE_ORGANIZATION_ROLE_MEMBERS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_role',
    dependencies: ['organization'],
    permissionKey: "api:organization_role:view"
  },

  // Permission Management
  {
    id: 'organization_permissions',
    name: 'Permission Management',
    envKey: 'FEATURE_ORGANIZATION_PERMISSIONS',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    dependencies: ['organization'],
    permissionKey: "api:organization_permissions:view"
  },

  {
    id: 'organization_permissions_view',
    name: 'View Role Permissions',
    envKey: 'FEATURE_ORGANIZATION_PERMISSIONS_VIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_permissions',
    dependencies: ['organization'],
    permissionKey: "api:organization_permissions:view"
  },

  {
    id: 'organization_permissions_edit',
    name: 'Grant and Revoke Permissions',
    envKey: 'FEATURE_ORGANIZATION_PERMISSIONS_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_permissions',
    dependencies: ['organization'],
    permissionKey: "api:organization_permissions:edit"
  },

  // Organization Resource Limits
  {
    id: 'organization_limits',
    name: 'Organization Resource Limits',
    envKey: 'FEATURE_ORGANIZATION_LIMITS',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    dependencies: ['organization'],
    permissionKey: "api:organization_limits:view"
  },

  {
    id: 'organization_limits_view',
    name: 'View Organization Limits',
    envKey: 'FEATURE_ORGANIZATION_LIMITS_VIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_limits',
    dependencies: ['organization'],
    permissionKey: "api:organization_limits:view"
  },

  {
    id: 'organization_limits_edit',
    name: 'Set Organization Limits',
    envKey: 'FEATURE_ORGANIZATION_LIMITS_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_limits',
    dependencies: ['organization'],
    permissionKey: "api:organization_limits:edit"
  },

  // User Resource Limits
  {
    id: 'organization_user_limits',
    name: 'User Resource Limits',
    envKey: 'FEATURE_ORGANIZATION_USER_LIMITS',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    dependencies: ['organization'],
    permissionKey: "api:organization_user_limits:view"
  },

  {
    id: 'organization_user_limits_view',
    name: 'View User Limits',
    envKey: 'FEATURE_ORGANIZATION_USER_LIMITS_VIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_user_limits',
    dependencies: ['organization'],
    permissionKey: "api:organization_user_limits:view"
  },

  {
    id: 'organization_user_limits_edit',
    name: 'Set User Limits',
    envKey: 'FEATURE_ORGANIZATION_USER_LIMITS_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_user_limits',
    dependencies: ['organization'],
    permissionKey: "api:organization_user_limits:edit"
  },

  // IP Range Limits
  {
    id: 'organization_ip_range_limit',
    name: 'IP Range Limits',
    envKey: 'FEATURE_ORGANIZATION_IP_RANGE_LIMIT',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    dependencies: ['organization'],
    permissionKey: "api:organization_ip_range_limit:view"
  },

  {
    id: 'organization_ip_range_limit_view',
    name: 'View IP Range Limits',
    envKey: 'FEATURE_ORGANIZATION_IP_RANGE_LIMIT_VIEW',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_ip_range_limit',
    dependencies: ['organization'],
    permissionKey: "api:organization_ip_range_limit:view"
  },

  {
    id: 'organization_ip_range_limit_edit',
    name: 'Set IP Range Limits',
    envKey: 'FEATURE_ORGANIZATION_IP_RANGE_LIMIT_EDIT',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'organization_ip_range_limit',
    dependencies: ['organization'],
    permissionKey: "api:organization_ip_range_limit:edit"
  },

  // ==========================================================================
  // HEADER/NAVBAR FEATURES (Not shown in sidebar, accessed via header)
  // ==========================================================================
  {
    id: 'header_notifications',
    name: 'Notifications',
    envKey: 'FEATURE_HEADER_NOTIFICATIONS',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
  },
  {
    id: 'notification_settings',
    name: 'Notification Settings',
    envKey: 'FEATURE_NOTIFICATION_SETTINGS',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'header_notifications',
    dependencies: ['header_notifications'],
    permissionKey: "api:notification_management:view"
  },
  {
    id: 'notification_settings_write',
    name: 'Notification Settings Write',
    envKey: 'FEATURE_NOTIFICATION_SETTINGS_WRITE',
    type: FeatureType.CHILD,
    orphanStatus: OrphanStatus.ORPHANED,
    defaultEnabled: true,
    parentId: 'notification_settings',
    dependencies: ['notification_settings'],
    permissionKey: "api:notification_management:edit"
  },

  // ==========================================================================
  // STANDALONE: CUSTOM DNS OVERRIDES (card on /crafted/account/settings)
  // ==========================================================================
  {
    id: 'dns_overrides',
    name: 'Custom DNS Overrides',
    envKey: 'FEATURE_DNS_OVERRIDES',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
    apiEndpoints: ['/dns-overrides/'],
    // No `route`, `component` or `sidebar`: this is a card inside
    // /crafted/account/settings, not a routed page. PrivateRoutes.tsx:75 skips
    // entries without a route, matching the routeless `notification_settings`.
    //
    // No `permissionKey`: the backend is IsAuthenticated-only and exposes no
    // permission scope for this resource, so any logged-in user who can see
    // the card can repoint DNS for every scan in the deployment. The card
    // carries a prominent warning saying exactly that.
    // See docs/superpowers/specs/2026-08-03-dns-overrides-design.md §1, §7
  },


  {
    id: 'header_fast_switch_org',
    name: 'Fast Switch Organization',
    envKey: 'FEATURE_HEADER_FAST_SWITCH_ORG',
    type: FeatureType.STANDALONE,
    defaultEnabled: true,
  },

  // ==========================================================================
  // STANDALONE: MOCK DATA (Development/Demo Feature)
  // ==========================================================================
  {
    id: 'mock_data',
    name: 'Mock Data',
    envKey: 'FEATURE_MOCK_DATA',
    type: FeatureType.STANDALONE,
    defaultEnabled: false,
  },

  // ==========================================================================
  // AGENT ENVIRONMENTS (restored; OFF by default — flip REACT_APP_FEATURE_AGENT_ENVIRONMENTS)
  // ==========================================================================
  {
    id: 'agent_environments',
    name: 'Agent Environments',
    envKey: 'FEATURE_AGENT_ENVIRONMENTS',
    type: FeatureType.STANDALONE,
    defaultEnabled: false,
    route: '/agent/*',
    component: lazy(() => import('../app/pages/agent')),
    sidebar: { show: true, icon: 'bot', order: 94 },
    apiEndpoints: [
      '/environments',
      '/environments/{id}',
      '/environments/user/',
      '/environments/{id}/analyze',
      '/environments/{id}/continue',
      '/environments/{id}/stream/{streamId}',
    ],
  },
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getFeatureById = (id: string): FeatureDefinition | undefined => {
  return FEATURE_CONFIG.find((f) => f.id === id)
}

export const getFeaturesByType = (type: FeatureType): FeatureDefinition[] => {
  return FEATURE_CONFIG.filter((f) => f.type === type)
}

export const getChildFeatures = (parentId: string): FeatureDefinition[] => {
  return FEATURE_CONFIG.filter((f) => f.parentId === parentId)
}

export const getParentFeature = (childId: string): FeatureDefinition | undefined => {
  const child = getFeatureById(childId)
  if (!child || !child.parentId) return undefined
  return getFeatureById(child.parentId)
}
